<template>
  <div class="w-full flex flex-wrap">
    <div class="md:w-2/3 w-full">
      <div class="mx-auto md:mt-16 w-full flex justify-center">
        <AppLogo class="rounded-2xl h-36 w-[80%]" />
      </div>
      <Head :title="__('Register to Onsho24')" />

      <form
        @submit.prevent="attempt"
        class="p-8 max-w-[30rem] mx-auto"
      >
        <h2 class="text-2xl text-center font-normal mb-4">
          {{ __('Start Your Free Trial') }}
        </h2>

        <div class="mb-6">
          <label class="block mb-2" for="company_name">
            {{ __('Company Name') }} <span class="text-red-500">*</span>
          </label>
          <input
            v-model="form.company_name"
            class="form-control form-input form-input-bordered w-full"
            :class="{ 'form-input-border-error': form.errors.has('company_name') }"
            id="company_name"
            type="text"
            name="company_name"
            :placeholder="__('Enter your company name')"
            required
          />
          <HelpText class="mt-2 text-red-500" v-if="form.errors.has('company_name')">
            {{ form.errors.first('company_name') }}
          </HelpText>
        </div>

        <div class="mb-6 grid grid-cols-2 gap-4">
          <div>
            <label class="block mb-2" for="first_name">
              {{ __('First Name') }}
            </label>
            <input
              v-model="form.first_name"
              class="form-control form-input form-input-bordered w-full"
              :class="{ 'form-input-border-error': form.errors.has('first_name') }"
              id="first_name"
              type="text"
              name="first_name"
              :placeholder="__('First Name')"
            />
            <HelpText class="mt-2 text-red-500" v-if="form.errors.has('first_name')">
              {{ form.errors.first('first_name') }}
            </HelpText>
          </div>

          <div>
            <label class="block mb-2" for="last_name">
              {{ __('Last Name') }}
            </label>
            <input
              v-model="form.last_name"
              class="form-control form-input form-input-bordered w-full"
              :class="{ 'form-input-border-error': form.errors.has('last_name') }"
              id="last_name"
              type="text"
              name="last_name"
              :placeholder="__('Last Name')"
            />
            <HelpText class="mt-2 text-red-500" v-if="form.errors.has('last_name')">
              {{ form.errors.first('last_name') }}
            </HelpText>
          </div>
        </div>

        <div class="mb-6">
          <label class="block mb-2" for="email">
            {{ __('Email') }} <span class="text-red-500">*</span>
          </label>
          <input
            v-model="form.email"
            class="form-control form-input form-input-bordered w-full"
            :class="{ 'form-input-border-error': form.errors.has('email') }"
            id="email"
            type="email"
            name="email"
            dir="ltr"
            placeholder="example@gmail.com"
            required
          />
          <HelpText class="mt-2 text-red-500" v-if="form.errors.has('email')">
            {{ form.errors.first('email') }}
          </HelpText>
        </div>

        <div class="mb-6">
          <label class="block mb-2" for="phone_number">
            {{ __('Phone Number') }} <span class="text-red-500">*</span>
          </label>
          <input
            v-model="form.phone_number"
            class="form-control form-input form-input-bordered w-full"
            :class="{ 'form-input-border-error': form.errors.has('phone_number') }"
            id="phone_number"
            type="text"
            name="phone_number"
            dir="ltr"
            placeholder="09123456789"
            required
          />
          <HelpText class="mt-2 text-red-500" v-if="form.errors.has('phone_number')">
            {{ form.errors.first('phone_number') }}
          </HelpText>
        </div>

        <div class="mb-6">
          <label class="block mb-2" for="password">
            {{ __('Password') }} <span class="text-red-500">*</span>
          </label>
          <div class="relative">
            <input
              v-model="form.password"
              class="form-control form-input form-input-bordered w-full pr-10"
              :class="{ 'form-input-border-error': form.errors.has('password') }"
              id="password"
              dir="ltr"
              :type="showPassword ? 'text' : 'password'"
              name="password"
              :placeholder="__('Enter your password')"
              required
            />
            <button
              type="button"
              @click="showPassword = !showPassword"
              class="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
            >
              <HeroiconsSolidEye v-if="!showPassword" class="w-5 h-5" />
              <HeroiconsSolidEyeOff v-else class="w-5 h-5" />
            </button>
          </div>
          <HelpText class="mt-2 text-red-500" v-if="form.errors.has('password')">
            {{ form.errors.first('password') }}
          </HelpText>
        </div>

        <div class="mb-6">
          <label class="block mb-2" for="password_confirmation">
            {{ __('Confirm Password') }} <span class="text-red-500">*</span>
          </label>
          <div class="relative">
            <input
              v-model="form.password_confirmation"
              class="form-control form-input form-input-bordered w-full pr-10"
              :class="{ 'form-input-border-error': form.errors.has('password_confirmation') }"
              id="password_confirmation"
              dir="ltr"
              :type="showPasswordConfirmation ? 'text' : 'password'"
              name="password_confirmation"
              :placeholder="__('Confirm your password')"
              required
            />
            <button
              type="button"
              @click="showPasswordConfirmation = !showPasswordConfirmation"
              class="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
            >
              <HeroiconsSolidEye v-if="!showPasswordConfirmation" class="w-5 h-5" />
              <HeroiconsSolidEyeOff v-else class="w-5 h-5" />
            </button>
          </div>
          <HelpText class="mt-2 text-red-500" v-if="form.errors.has('password_confirmation')">
            {{ form.errors.first('password_confirmation') }}
          </HelpText>
        </div>

        <Button
          class="w-full flex justify-center rounded-lg bg-primary-500 hover:bg-primary-600 text-white"
          type="submit"
          :loading="form.processing"
        >
          <span>
            {{ __('Start your 14-day free trial') }}
          </span>
        </Button>

        <div class="mt-6 text-center">
          <span class="text-gray-600">{{ __('Already have an account?') }}</span>
          <Link
            :href="$url('/login')"
            class="text-primary-500 font-bold underline hover:text-primary-600 ml-1"
          >
            {{ __('Login') }}
          </Link>
        </div>
      </form>
    </div>

    <div class="md:w-1/3 md:block hidden h-screen">
      <img src="/images/login-banner.png" alt="Register Image" class="w-full h-screen object-contain">
    </div>
  </div>
</template>

<script>
import Auth from '@/layouts/Auth'
import { Button } from 'laravel-nova-ui'
import { Link, Head } from '@inertiajs/inertia-vue3'
import HeroiconsSolidEye from '@/components/Heroicons/solid/HeroiconsSolidEye'
import HeroiconsSolidEyeOff from '@/components/Heroicons/solid/HeroiconsSolidEyeOff'

export default {
  name: 'RegisterPage',

  layout: Auth,

  components: {
    Button,
    Link,
    Head,
    HeroiconsSolidEye,
    HeroiconsSolidEyeOff,
  },

  data: () => ({
    showPassword: false,
    showPasswordConfirmation: false,
    form: Nova.form({
      company_name: '',
      first_name: '',
      last_name: '',
      email: '',
      phone_number: '',
      password: '',
      password_confirmation: '',
    }),
  }),

  mounted() {
    // نمایش flash messages از session
    if (this.$page.props.flash?.error) {
      Nova.error(this.$page.props.flash.error)
    }
    if (this.$page.props.flash?.success) {
      Nova.success(this.$page.props.flash.success)
    }
  },

  methods: {
    async attempt() {
      try {
        const { redirect } = await this.form.post(Nova.url('/tenant/register'))

        let path = { url: Nova.url('/'), remote: true }

        if (redirect !== undefined && redirect !== null) {
          path = { url: redirect, remote: true }
        }

        Nova.visit(path)
      } catch (error) {
        if (error.response?.status === 500) {
          Nova.error(this.__('There was a problem submitting the form.'))
        }
      }
    },
  },
}
</script>
